import {List, Select, Text} from '@gwc/core'
import {Box, TextInput, Typography} from '@gwc/main'
import {get, isEqual} from '@yandex-delivery/utils'
import clsx from 'clsx'
import {useCallback, useMemo} from 'react'

import {PlusIcon} from '../icons'
import {inputClasses} from '../input/Input.classes'
import {Loader} from '../loader'
import {slotFromArray, useSlots} from '../slot'
import {IntrinsicInputProps} from '../types'
import {classes} from './Select.classes'
import {SelectOptionItem, SelectProps} from './Select.types'

const defaultRenderText = (option: SelectOptionItem<unknown>) => <List.Label variant="body2">{option.label}</List.Label>
const defaultGetText = get('label')

const renderOption =
  <T,>(
    makeKey: (o: SelectOptionItem<T>) => string,
    renderText: (o: SelectOptionItem<T>) => JSX.Element,
    isReadonly = false,
  ) =>
  (option: SelectOptionItem<T>) =>
    (
      <List.CollectionItem key={makeKey(option)} textValue={option.label}>
        <List.Item className={clsx(classes.noBorderListItem, isReadonly && classes.noActionSelectItem)}>
          <Text>{renderText(option)}</Text>
          <List.CheckTrail />
        </List.Item>
      </List.CollectionItem>
    )

const renderCreateOption = (text: React.ReactNode) => (key: string) => {
  return (
    <List.CollectionItem key={key}>
      <List.Item>
        <Text>
          <List.Label variant="body2">{text}</List.Label>
        </Text>
      </List.Item>
    </List.CollectionItem>
  )
}

const combineEffects =
  <Params extends unknown[]>(f1?: (...args: Params) => void, f2?: (...args: Params) => void) =>
  (...args: Params) => {
    f1?.(...args)
    f2?.(...args)
  }

/** мерджим объекты, переопределяя ивент листенеры так чтобы работали оба */
const mergeInputProps = (props1: IntrinsicInputProps, props2?: IntrinsicInputProps): IntrinsicInputProps => {
  if (!props2) return props1

  const naivelyMerged = {...props1, ...props2}

  for (const key in naivelyMerged) {
    // определяем ивент листенеры
    if (/^on[A-Z].*/.test(key)) {
      naivelyMerged[key as keyof typeof naivelyMerged] = combineEffects(
        props1[key as keyof typeof props1],
        props2[key as keyof typeof props2],
      )
    }
  }

  return naivelyMerged
}

const Selectv2 = function Selectv2<T, Mode extends 'single' | 'multi' = 'single'>({
  isPending,
  value,
  options,
  onChange,
  label,
  noAutocomplete,
  helperText,
  className,
  lead: leadProp,
  trail: trailProp,
  'data-testid': dataTestId,
  inputProps: passedInputProps,
  makeKey = defaultGetText,
  makeText = defaultGetText,
  clearable,
  creatable,
  createItemText = <PlusIcon />,
  renderOptionText = defaultRenderText,
  hideToggleIcon = false,
  multi,
  noOptionsText,
  defaultValue,
  getSelectedOption,
  ...props
}: SelectProps<T, Mode>) {
  const isEmpty = options.length === 0
  // @ts-expect-error тут нужен только label, потому что клик на искусственный элемент отключен
  const displayedOptions: SelectOptionItem<T>[] = isEmpty
    ? [
        {
          label: noOptionsText || 'Ничего не найдено',
          value: null,
        },
      ]
    : options
  // TODO надо это делать на уровне GWC в идеале, для чего синхронизировать с местными дизайнерами как должен себя вести селект.
  // Если опций нет, мы эмулируем некликабельную опцию, для чего оверрайдим функции, поступающие из пропсов.
  const appliedMakeKey = isEmpty ? defaultGetText : makeKey
  const appliedMakeText = isEmpty ? defaultGetText : makeText
  const appliedRenderOptionText = isEmpty ? defaultRenderText : renderOptionText

  const selectTrail = useMemo(() => (isPending ? <Loader /> : <Select.Toggle />), [isPending])

  const selectedOption = useMemo(
    () => {
      if (typeof getSelectedOption === 'function') {
        if (multi) {
          return getSelectedOption(options, value as T[] | null | undefined)
        } else {
          return getSelectedOption(options, value as T | null | undefined)
        }
      }

      return (
        multi
          ? options.filter(option => value?.includes(option.value))
          : options.find(option => isEqual(option.value, value))
      ) || null
    },
    [options, multi, value, getSelectedOption],
  )

  const {lead, trail} = useSlots({
    lead: leadProp,
    trail: slotFromArray([clearable && <Select.Clear />, trailProp, !hideToggleIcon && selectTrail]),
  })

  const changeHandler = useCallback(
    (value: SelectOptionItem<T> | SelectOptionItem<T>[] | null) => {
      // Если пользователь пытается очистить селект, когда он не clearable, не позволяем
      // TODO остаётся корнеркейс - когда селект clearable, его можно очистить выбрав тот же итем. Программно непонятно как различить такую очистку и клик по крестику, и непонятно нужно ли
      const shouldApplyChange = value !== null || clearable

      if (shouldApplyChange) {
        multi ? onChange?.(value as SelectOptionItem<T>[]) : onChange?.(value as SelectOptionItem<T>)
      }
    },
    [multi, onChange, clearable],
  )

  return (
    <>
      <Select<SelectOptionItem<T>>
        className={clsx(className, inputClasses.textField)}
        creatable={creatable && {...creatable, render: renderCreateOption(createItemText)}}
        makeKey={appliedMakeKey}
        makeText={appliedMakeText}
        onChange={changeHandler}
        openOnFocus={false}
        options={displayedOptions}
        popperProps={{className: classes.popper}}
        renderOption={renderOption(appliedMakeKey, appliedRenderOptionText, isEmpty)}
        value={defaultValue && !selectedOption ? {value: defaultValue, label: ''} : selectedOption}
        variant="outline"
        multi={multi}
        {...props}
      >
        {({inputProps: {variant, value, ...inputProps}, labelProps}) => {
          const merged = mergeInputProps(inputProps, passedInputProps)
          const baseValue = merged.value === undefined ? value : merged.value

          return (
            <>
              {lead}
              <Text data-testid={dataTestId}>
                {!!label && <Typography {...labelProps}>{label}</Typography>}
                <TextInput
                  {...merged}
                  autoComplete="new-password"
                  readOnly={noAutocomplete}
                  value={
                    multi && Array.isArray(selectedOption) ? selectedOption.map(el => el.label).join(', ') : baseValue
                  }
                  variant={variant}
                />
              </Text>
              {trail}
            </>
          )
        }}
      </Select>
      {helperText && (
        <Box pt="xs">
          <Typography color={props.error ? 'error' : 'minor'} variant="caption1">
            {helperText}
          </Typography>
        </Box>
      )}
    </>
  )
}

export default Selectv2
